/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useMemo, useRef } from 'react';

import { GuideProgram } from 'client-code/filtered-guide';

import { ErrorTypes } from '../../../../core/error';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { Keys, ScreenTaskControllerTypes } from '../../../../core/navigation';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import {
    resetFilteredGuideIndexes,
    setFilteredGuideSelectedProgram,
} from '../../../../store/guide/filteredguide/filteredguide.reducers';
import { onSetVerticalPositionFilteredGuideAction } from '../../../../store/guide/filteredguide/navigation/filteredguide.navigation.actions';
import { showWhisper } from '../../../../store/whisper/whisper.reducer';
import { WhisperTypes } from '../../../Whisper/WhisperTypes';
import {
    FILTERED_GUIDE_FETCH_NUMBER,
    FILTERED_GUIDE_MAX_SHOWS_PER_PAGE,
} from '../filteredguide.constants';
import { useFilteredGuideFetch } from '../hooks/useFilteredGuideFetch';
import { useKeyPressAction } from '../hooks/useKeyPressAction';
import { useTurboScroll } from '../hooks/useTurboScroll';
import {
    getLastCompletedHour,
    getLastProgramIndex,
    IFilteredGuideTableProps,
} from './filteredguidetable.utils';
import FilteredGuideTableUI from './FilteredGuideTableUI';

import './FilteredGuideTable.scss';

const handleChannelKeyPress = (
    isError: boolean,
    dispatch: Function,
    fetchInitialFilteredGuideContent: Function,
    category: string,
    channelsType: string,
    pageState: any,
    direction: 'up' | 'down',
    decrementPage: Function,
    incrementPage: Function,
) => {
    if (isError) {
        const whisperMessage = `${Locale.getString(
            LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
        )}(${ErrorTypes.FILTERED_GUIDE_PROGRAMS_APIGW_5XX_C2202})`;
        dispatch(
            showWhisper({
                message: whisperMessage,
                iconType: WhisperTypes.NO_ICON,
            }),
        );
        fetchInitialFilteredGuideContent(category, channelsType);
        return;
    }
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }

    const targetPageIndex =
        direction === 'up' ? pageState.currentPageIndex - 1 : pageState.currentPageIndex + 1;

    if (pageState.pages.has(targetPageIndex)) {
        // eslint-disable-next-line no-unused-expressions
        direction === 'up' ? decrementPage() : incrementPage();
    }
};

const FilteredGuideTable = ({
    category,
    channelsType,
    allPossibleChannels,
}: IFilteredGuideTableProps) => {
    const { verticalPosition, isError } = useTivoSelector(
        (state: RootState) => state.filteredGuide,
    );
    const dispatch = useTivoDispatch();
    const { screenTaskController } = useTivoSelector((state: RootState) => state.screens);

    const jumpToLatest = useRef(false);
    const negativeOffsetRef = useRef(-2 * FILTERED_GUIDE_FETCH_NUMBER);
    const positiveOffsetRef = useRef(
        FILTERED_GUIDE_FETCH_NUMBER + FILTERED_GUIDE_MAX_SHOWS_PER_PAGE,
    );

    const allPossibleChannelsRef = useRef(allPossibleChannels);

    const {
        pageState,
        setPageState,
        minStartTimestamp,
        maxStartTimestamp,
        startTimestamp,
        cacheRequestToken,
        fetchFilteredGuideContent,
        fetchInitialFilteredGuideContent,
    } = useFilteredGuideFetch(allPossibleChannelsRef);

    const onNetworkStateChangeCallback = () => {
        if (NetworkManager.isNetworkDisconnected()) {
            setPageState(() => ({
                pages: new Map(),
                currentPageIndex: 0,
                previousPageIndex: 0,
            }));
            dispatch(resetFilteredGuideIndexes());
        } else {
            startTimestamp.current = getLastCompletedHour();
            fetchInitialFilteredGuideContent(category, channelsType, false, undefined);
        }
    };

    const decrementPage = () => {
        jumpToLatest.current = true;
        setPageState((prev) => ({
            ...prev,
            currentPageIndex: prev.currentPageIndex - 1,
            previousPageIndex: prev.currentPageIndex,
        }));
    };

    const incrementPage = () => {
        dispatch(onSetVerticalPositionFilteredGuideAction(0));
        setPageState((prev) => ({
            ...prev,
            currentPageIndex: prev.currentPageIndex + 1,
            previousPageIndex: prev.currentPageIndex,
        }));
    };

    useKeyPressAction(
        [
            Keys.KEYBOARD_KEY_CHANNEL_UP,
            Keys.LG_REMOTE_KEY_CHANNEL_UP,
            Keys.SAMSUNG_REMOTE_KEY_CHANNEL_UP,
        ],
        () =>
            handleChannelKeyPress(
                isError,
                dispatch,
                fetchInitialFilteredGuideContent,
                category,
                channelsType,
                pageState,
                'up',
                decrementPage,
                incrementPage,
            ),
        screenTaskController === ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS,
    );

    useKeyPressAction(
        [
            Keys.KEYBOARD_KEY_CHANNEL_DOWN,
            Keys.LG_REMOTE_KEY_CHANNEL_DOWN,
            Keys.SAMSUNG_REMOTE_KEY_CHANNEL_DOWN,
        ],
        () =>
            handleChannelKeyPress(
                isError,
                dispatch,
                fetchInitialFilteredGuideContent,
                category,
                channelsType,
                pageState,
                'down',
                decrementPage,
                incrementPage,
            ),
        screenTaskController === ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS,
    );

    const { guideProgramList, lastGuideProgramIndex } = useMemo(() => {
        const programs = pageState.pages.get(pageState.currentPageIndex) || [];
        const lastProgramIndex = getLastProgramIndex(programs);

        return {
            guideProgramList: programs,
            lastGuideProgramIndex: lastProgramIndex,
        };
    }, [pageState]);

    useEffect(() => {
        if (jumpToLatest.current) {
            jumpToLatest.current = false;
            dispatch(onSetVerticalPositionFilteredGuideAction(lastGuideProgramIndex));
        }
    }, [guideProgramList, lastGuideProgramIndex, dispatch]);

    useEffect(() => {
        const networkManagerRef = NetworkManager.subscribe(onNetworkStateChangeCallback);

        fetchInitialFilteredGuideContent(category, channelsType);

        return () => {
            NetworkManager.unsubscribe(networkManagerRef);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep allPossibleChannelsRef in sync and re-fetch when channel data becomes
    // available so that UHD/PPV channels are properly filtered from the guide.
    const channelsProcessedRef = useRef(false);
    useEffect(() => {
        allPossibleChannelsRef.current = allPossibleChannels;
        if (allPossibleChannels.length > 0 && !channelsProcessedRef.current) {
            channelsProcessedRef.current = true;
            fetchInitialFilteredGuideContent(category, channelsType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPossibleChannels]);

    useEffect(() => {
        const pagesKeys = Array.from(pageState.pages.keys());
        if (pagesKeys.length === 0 || pageState.currentPageIndex === 0) return;

        const minPageIndex = Math.min(...pagesKeys);
        const maxPageIndex = Math.max(...pagesKeys);

        const isNearStart = pageState.currentPageIndex === minPageIndex + 1;
        const isNearEnd = pageState.currentPageIndex === maxPageIndex - 1;
        const goesToFuture = pageState.currentPageIndex > pageState.previousPageIndex;

        if (goesToFuture && isNearEnd) {
            const offset = positiveOffsetRef.current;
            positiveOffsetRef.current += FILTERED_GUIDE_FETCH_NUMBER;
            fetchFilteredGuideContent(category, channelsType, offset);
        } else if (!goesToFuture && isNearStart) {
            const offset = negativeOffsetRef.current;
            negativeOffsetRef.current -= FILTERED_GUIDE_FETCH_NUMBER;
            fetchFilteredGuideContent(category, channelsType, offset);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageState.currentPageIndex]);

    useEffect(() => {
        if (verticalPosition === -1) {
            if (pageState.pages.has(pageState.currentPageIndex - 1)) {
                decrementPage();
            } else {
                dispatch(onSetVerticalPositionFilteredGuideAction(0));
            }
        }
        if (verticalPosition === lastGuideProgramIndex + 1) {
            if (pageState.pages.has(pageState.currentPageIndex + 1)) {
                incrementPage();
            } else {
                dispatch(onSetVerticalPositionFilteredGuideAction(lastGuideProgramIndex));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verticalPosition]);

    const { turboScrollCurrentDisplayedDate, filteredGuideTurboScroll } = useTurboScroll({
        category,
        channelsType,
        guideProgramList,
        minStartTimestamp: minStartTimestamp.current,
        maxStartTimestamp: maxStartTimestamp.current,
        startTimestamp: startTimestamp.current,
        cacheRequestToken: cacheRequestToken.current,
        onFetchInitialFilteredGuideContent: fetchInitialFilteredGuideContent,
        onSetPageState: setPageState,
        onSetStartTimestamp: (timestamp) => {
            startTimestamp.current = timestamp;
        },
    });

    useEffect(() => {
        if (guideProgramList && !filteredGuideTurboScroll) {
            const selectedProgram = guideProgramList.filter((p) => p.type === 'program')?.[
                verticalPosition
            ];
            if (selectedProgram) {
                dispatch(setFilteredGuideSelectedProgram(selectedProgram.value as GuideProgram));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verticalPosition, guideProgramList, dispatch]);

    return (
        <div className="filtered-guide-content">
            <FilteredGuideTableUI
                category={category}
                allPossibleChannels={allPossibleChannels}
                currentPageData={guideProgramList}
                pageIndex={pageState.currentPageIndex}
                isTurboScrollEnabled={filteredGuideTurboScroll}
                turboScrollCurrentDisplayedDate={turboScrollCurrentDisplayedDate}
            />
        </div>
    );
};

export default FilteredGuideTable;
